import Head from "next/head"
import Link from "next/link"
import { BlitzPage, Routes } from "@blitzjs/next"
import { useQuery, usePaginatedQuery } from "@blitzjs/rpc"
import { useState, useEffect } from "react"
import { User, Session } from "db"
import Layout from "app/core/layouts/Layout"
import Suspense from "app/core/components/Suspense"
import debounce from "lodash/debounce"
import { isValid, startOfDay, endOfDay, subDays } from "date-fns"
import { calcZonedDate, formatDateTime } from "app/date"

import getPaginatedOrders from "app/orders/queries/getPaginatedOrders"
import getFilterData from "app/pipelines/queries/getFilterData"

import Container from "@mui/material/Container"
import Typography from "@mui/material/Typography"
import Box from "@mui/material/Box"
import Card from "@mui/material/Card"
import Grid from "@mui/material/Grid"
import Checkbox from "@mui/material/Checkbox"
import FormGroup from "@mui/material/FormGroup"
import FormControlLabel from "@mui/material/FormControlLabel"
import TextField from "@mui/material/TextField"
import SearchIcon from "@mui/icons-material/Search"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid"
import { DateRangePicker } from "app/core/components/DateRangePicker"
import IconButton from "@mui/material/IconButton"
import { Order } from "@prisma/client"
import Autocomplete from "app/core/components/Autocomplete/Autocomplete"
import AutocompleteMulti from "app/core/components/Autocomplete/MultipleAutocomplete"
import { set } from "lodash"

type UserType = User & {
  session: Session
}

interface UsersListProps {
  search: Object
}
const ITEMS_PER_PAGE = 100

type PaginatedOrder = Awaited<ReturnType<typeof getPaginatedOrders>>["orders"][number]

export const UsersList = ({ search }: UsersListProps) => {
  const [rows, setRows] = useState<PaginatedOrder[]>([])
  const [page, setPage] = useState<number>(0)
  const [{ orders, count: rowCount }, { isFetching }] = usePaginatedQuery(getPaginatedOrders, {
    orderBy: { id: "desc" },
    where: search,
    skip: ITEMS_PER_PAGE * page,
    take: ITEMS_PER_PAGE,
  })

  const columns: GridColDef[] = [
    {
      field: "firstName",
      headerName: "First Name",
      flex: 1,
      sortable: false,
      valueGetter: (params) => params.row.firstName,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      sortable: false,
      valueGetter: (params) => params.row.email,
    },
    {
      field: "phone",
      headerName: "Phone No.",
      flex: 1,
      sortable: false,
      valueGetter: (params) => params.row.phone,
    },
    {
      field: "age",
      headerName: "Age",
      flex: 1,
      sortable: false,
      valueGetter: (params) => params.row.age,
    },
    {
      field: "heightCm",
      headerName: "Height",
      flex: 1,
      sortable: false,
      valueGetter: (params) => params.row.heightCm,
    },
    {
      field: "weightKg",
      headerName: "Weight",
      flex: 1,
      sortable: false,
      valueGetter: (params) => params.row.weightKg,
    },
    {
      field: "deltaWeightKg",
      headerName: "Weight Delta",
      flex: 1,
      sortable: false,
      valueGetter: (params) => params.row.deltaWeightKg,
    },
    {
      field: "pipeline",
      headerName: "Last Pipeline",
      flex: 1,
      sortable: false,
      valueGetter: (params) => params.row.pipeline.name,
    },
    {
      field: "updatedAt",
      sortable: false,
      headerName: "Updated",
      flex: 1,
      valueGetter: (params) => formatDateTime(params.row.updatedAt),
    },
    {
      field: "language",
      sortable: false,
      headerName: "Locale",
      flex: 1,
      valueGetter: (params) => params.row.language.code,
    },
    {
      field: "actions",
      type: "actions",
      renderCell: (order: GridRenderCellParams<any, Order>) => (
        <Link href={Routes.OrderPage({ orderId: order.id })} title="Open order" legacyBehavior>
          <a target="_blank" rel="noopener noreferrer">
            <IconButton>
              <OpenInNewIcon />
            </IconButton>
          </a>
        </Link>
      ),
    },
  ]

  useEffect(() => {
    if (orders == undefined) return

    setRows(orders)
  }, [orders])

  return (
    <>
      <div style={{ height: 400, width: "100%", backgroundColor: "white" }}>
        <DataGrid
          loading={isFetching}
          rows={rows}
          rowCount={rowCount}
          columns={columns}
          pageSize={ITEMS_PER_PAGE}
          rowsPerPageOptions={[ITEMS_PER_PAGE]}
          paginationMode="server"
          onPageChange={(newPage) => setPage(newPage)}
        />
      </div>
    </>
  )
}

enum DateTargets {
  leadAt = "leadAt",
  updatedAt = "updatedAt",
  completedAt = "completedAt",
}

const OrderFilters = ({ search, onSearch }) => {
  const [dateTarget, setDateTarget] = useState(DateTargets.leadAt)
  const [startDate, setStartDate] = useState<Date | undefined>(new Date())
  const [endDate, setEndDate] = useState<Date | undefined>(new Date())

  const [filterData] = useQuery(getFilterData, undefined, { staleTime: Infinity })

  const debouncedSearch = debounce(onSearch, 350)

  const handleCheckboxChange = (value: DateTargets) => {
    setDateTarget(value)
    onSearch((prevState) => {
      const prevStateDateTargetVal = prevState[dateTarget]
      delete prevState[dateTarget]

      return { ...prevState, [value]: prevStateDateTargetVal }
    })
  }

  const handleStartDateChange = (date?: Date) => {
    setStartDate(date)
    onSearch((prevState) => {
      if (date && isValid(date)) {
        date = calcZonedDate(date, startOfDay)
        return {
          ...prevState,
          [dateTarget]: {
            ...prevState[dateTarget],
            gte: date,
          },
        }
      } else {
        delete prevState[dateTarget].gte

        return { ...prevState }
      }
    })
  }

  const handleEndDateChange = (date?: Date) => {
    setEndDate(date)
    onSearch((prevState) => {
      if (date && isValid(date)) {
        date = calcZonedDate(date, endOfDay)
        return {
          ...prevState,
          [dateTarget]: {
            ...prevState[dateTarget],
            lte: date,
          },
        }
      } else {
        delete prevState[dateTarget].lte

        return { ...prevState }
      }
    })
  }

  const handleOnSearch = ({ field, value }) => {
    const result = {}
    set(result, field, value)

    onSearch((prevState) => ({
      ...prevState,
      ...result,
    }))
  }

  return (
    <Card sx={{ mb: 4, p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            display="grid"
            sx={{
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(5, 1fr)",
                xl: "repeat(5, 1fr)",
              },
            }}
            gap={2}
            alignItems="center"
          >
            <Autocomplete
              title="Brand"
              collection={filterData.brands}
              optionValue={({ id }) => id}
              optionLabel={(brand) => brand!["name"]}
              optionKey="id"
              field="brandId"
              onChange={handleOnSearch}
            />
            <Autocomplete
              title="Purchase status"
              collection={[
                { name: "Paid", value: "PURCHASE" },
                { name: "Unpaid", value: { not: "PURCHASE" } },
              ]}
              optionValue={({ value }) => value}
              optionLabel={({ name }) => name}
              optionKey="value"
              field="status"
              onChange={handleOnSearch}
            />

            <AutocompleteMulti
              title="Geography"
              collection={filterData.geographies}
              optionValue={({ id }) => id}
              optionLabel={(geography) => geography.name}
              field="pipeline.geographyId.in"
              optionKey="name"
              onChange={({ field, value }) => handleOnSearch({ field, value })}
            />
            <AutocompleteMulti
              title="Language"
              collection={filterData.languages}
              optionValue={({ id }) => id}
              optionLabel={(language) => language.code}
              field="languageId.in"
              optionKey="code"
              onChange={({ field, value }) => handleOnSearch({ field, value })}
            />
            <Autocomplete
              title="Series"
              collection={filterData.githubRepositoryUrls}
              optionValue={(url) => url}
              optionLabel={(url) => new URL(url).pathname.substr(1)}
              field="pipeline.githubRepositoryUrl"
              optionKey=""
              onChange={({ field, value }) => handleOnSearch({ field, value })}
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container rowSpacing={1} style={{ marginTop: 20 }}>
        <Grid item xs={12}>
          <Box
            display="grid"
            sx={{
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(5, 1fr)",
                xl: "repeat(5, 1fr)",
              },
            }}
            gap={2}
            alignItems="center"
          >
            <Autocomplete
              title="Dates"
              collection={[
                { name: "Today", value: { gte: calcZonedDate(new Date(), startOfDay) } },
                {
                  name: "Last 30 days",
                  value: {
                    gte: calcZonedDate(subDays(new Date(), 30), startOfDay),
                  },
                },
                {
                  name: "Last 90 days",
                  value: {
                    gte: calcZonedDate(subDays(new Date(), 90), startOfDay),
                  },
                },
              ]}
              optionValue={({ value }) => value}
              optionLabel={({ name }) => name}
              defaultValue={{
                name: "Last 30 days",
                value: {
                  gte: calcZonedDate(subDays(new Date(), 30), startOfDay),
                },
              }}
              optionKey="value"
              field={dateTarget}
              onChange={handleOnSearch}
            />

            <DateRangePicker
              values={{ startDate, endDate }}
              onChange={({ startDate, endDate }) => {
                handleStartDateChange(startDate)
                handleEndDateChange(endDate)
              }}
            />
            <Box
              sx={{
                gridColumn: {
                  xs: "span 1",
                  sm: "span 2",
                },
              }}
            >
              <FormGroup sx={{ display: "flex", flexDirection: "row" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dateTarget === DateTargets.leadAt}
                      onChange={() => handleCheckboxChange(DateTargets.leadAt)}
                      sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                    />
                  }
                  label={<Typography sx={{ fontSize: 16 }}>For user origin</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dateTarget === DateTargets.updatedAt}
                      onChange={() => handleCheckboxChange(DateTargets.updatedAt)}
                      sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                    />
                  }
                  label={<Typography sx={{ fontSize: 16 }}>For last update</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dateTarget === DateTargets.completedAt}
                      onChange={() => handleCheckboxChange(DateTargets.completedAt)}
                      sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                    />
                  }
                  label={<Typography sx={{ fontSize: 16 }}>For purchase status</Typography>}
                />
              </FormGroup>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item md={2} xs={6}>
          <TextField
            id="outlined-basic"
            size="small"
            label="Pipeline name"
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            InputProps={{ endAdornment: <SearchIcon sx={{ ml: 1 }} color="action" /> }}
            onChange={(e) =>
              debouncedSearch({
                ...search,
                pipeline: { ...(search.pipeline || {}), name: { contains: e.target.value } },
              })
            }
          />
        </Grid>
        <Grid item md={2} xs={6}>
          <TextField
            id="outlined-basic"
            size="small"
            label="UTM Source"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            InputProps={{ endAdornment: <SearchIcon sx={{ ml: 1 }} color="action" /> }}
            onChange={(e) =>
              debouncedSearch({ ...search, utmSource: { contains: e.target.value || undefined } })
            }
          />
        </Grid>
        <Grid item md={2} xs={6}>
          <TextField
            id="outlined-basic"
            size="small"
            label="UTM Medium"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            InputProps={{ endAdornment: <SearchIcon sx={{ ml: 1 }} color="action" /> }}
            onChange={(e) =>
              debouncedSearch({ ...search, utmMedium: { contains: e.target.value || undefined } })
            }
          />
        </Grid>
        <Grid item md={2} xs={6}>
          <TextField
            id="outlined-basic"
            size="small"
            label="UTM Campaign"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            InputProps={{ endAdornment: <SearchIcon sx={{ ml: 1 }} color="action" /> }}
            onChange={(e) =>
              debouncedSearch({ ...search, utmCampaign: { contains: e.target.value || undefined } })
            }
          />
        </Grid>
        <Grid item md={2} xs={6}>
          <TextField
            id="outlined-basic"
            size="small"
            label="UTM Content"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            InputProps={{ endAdornment: <SearchIcon sx={{ ml: 1 }} color="action" /> }}
            onChange={(e) =>
              debouncedSearch({ ...search, utmContent: { contains: e.target.value || undefined } })
            }
          />
        </Grid>
        <Grid item md={2} xs={6}>
          <TextField
            id="outlined-basic"
            size="small"
            label="UTM Term"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            InputProps={{ endAdornment: <SearchIcon sx={{ ml: 1 }} color="action" /> }}
            onChange={(e) =>
              debouncedSearch({ ...search, utmTerm: { contains: e.target.value || undefined } })
            }
          />
        </Grid>
      </Grid>
    </Card>
  )
}

const OrdersPage: BlitzPage = () => {
  const [search, setSearch] = useState(() => ({
    leadAt: { gte: calcZonedDate(subDays(new Date(), 30), startOfDay) },
  }))

  return (
    <>
      <Head>
        <title>Orders</title>
      </Head>

      <Container maxWidth={false}>
        <Box sx={{ m: 1, display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Typography sx={{ my: 4, flexGrow: 1 }} variant="h4" component="h2">
            Orders
          </Typography>
        </Box>

        <Suspense>
          <OrderFilters search={search} onSearch={setSearch} />
        </Suspense>

        <Suspense>
          <UsersList search={search} />
        </Suspense>
      </Container>
    </>
  )
}

OrdersPage.authenticate = true
OrdersPage.getLayout = (page) => <Layout>{page}</Layout>

export default OrdersPage
